import { GetInitialDataCtx, WidgetProps } from "../../contracts/widget"
import { propertyMapWidgetConfig, PropertyMapWidgetConfig } from "./config"
import { WidgetProvider } from "../../providers/widget"
import { PropertyMapContent } from "./components/PropertyMapContent"
import WidgetSection from "../../components/WidgetSection"
import "./property-map.css"
import { useMemo } from "react"
import { resolveWidgetSpacingStyle } from "@homerunner-next/widget-core/spacing"
import { parseWidgetConfig } from "@homerunner-next/widget-core/schema"
import { hostFontSizeCss } from "../../common/host-scope"

/**
 * Dark-palette overrides for the auto first-paint path (#900). The base cssVars
 * block always emits the LIGHT palette (byte-identical); this appends the dark
 * --hfse-* set under :host([data-hr-theme="dark"]) (marker/post-hydration) and,
 * inside @media(prefers-color-scheme:dark), :host([data-hr-scheme="auto"]:not([data-hr-theme]))
 * (auto, no JS/cookie). Both conditions stay INSIDE :host().
 */
function propertyMapDarkOverrides(prefix: string, dc: PropertyMapWidgetConfig['darkModeColors']): string {
  return `
    ${prefix} .hfse-property-map-wrapper {
      --hfse-accent: ${dc.accent};
      --hfse-foreground: ${dc.foreground};
      --hfse-muted-foreground: ${dc.mutedForeground};
      --hfse-background: ${dc.background};
      --hfse-card-bg: ${dc.cardBackground};
      --hfse-card-fg: ${dc.cardForeground};
      --hfse-input-bg: ${dc.inputBackground};
      --hfse-input-fg: ${dc.inputForeground};
      --hfse-input-placeholder: ${dc.inputPlaceholder};
      --hfse-border: ${dc.borderColor};
      --hfse-btn-bg: ${dc.buttonBackground};
      --hfse-btn-fg: ${dc.buttonForeground};
      color: ${dc.foreground};
      color-scheme: dark;
    }
  `
}

export const getInitialData = async ({ options, feedId, widgetId }: GetInitialDataCtx<PropertyMapWidgetConfig>) => {
  return {}
}

export default function PropertyMapWidget({ options, feedId, widgetId, resolvedTheme, data }: WidgetProps<PropertyMapWidgetConfig>) {
  const parsedOptions = useMemo(() => parseWidgetConfig(propertyMapWidgetConfig, options), [options])
  const colors = useMemo(() => {
    return resolvedTheme === 'dark' ? parsedOptions.darkModeColors : parsedOptions.lightModeColors
  }, [resolvedTheme, parsedOptions.lightModeColors, parsedOptions.darkModeColors])

  const cssVars = useMemo(() => `
    .hfse-property-map-wrapper, .hfse-property-map-wrapper * {
      font-family: '${parsedOptions.font.family}' !important;
    }
    ${hostFontSizeCss(widgetId, parsedOptions.font.size)}
    .hfse-property-map-wrapper {
      --hfse-accent: ${colors.accent};
      --hfse-foreground: ${colors.foreground};
      --hfse-muted-foreground: ${colors.mutedForeground};
      --hfse-background: ${colors.background};
      --hfse-card-bg: ${colors.cardBackground};
      --hfse-card-fg: ${colors.cardForeground};
      --hfse-input-bg: ${colors.inputBackground};
      --hfse-input-fg: ${colors.inputForeground};
      --hfse-input-placeholder: ${colors.inputPlaceholder};
      --hfse-border: ${colors.borderColor};
      --hfse-btn-bg: ${colors.buttonBackground};
      --hfse-btn-fg: ${colors.buttonForeground};
      color: ${colors.foreground};
      ${resolvedTheme === 'dark' ? 'color-scheme: dark;' : ''}
    }
    ${propertyMapDarkOverrides(':where(:host([data-hr-theme="dark"]))', parsedOptions.darkModeColors)}
    @media (prefers-color-scheme: dark) {
      ${propertyMapDarkOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', parsedOptions.darkModeColors)}
    }
    ${parsedOptions.customCss ?? ''}
  `, [widgetId, parsedOptions.font, colors, resolvedTheme, parsedOptions.darkModeColors, parsedOptions.customCss])

  return (
    <WidgetProvider options={parsedOptions} feedId={feedId} widgetId={widgetId} resolvedTheme={resolvedTheme} widgetType="property-map" data={data ?? {}}>
      <div
        className={`hfse-property-map-wrapper${resolvedTheme === 'dark' ? ' dark' : ''}`}
        style={resolveWidgetSpacingStyle(parsedOptions)}
      >
        <style>{cssVars}</style>
        <WidgetSection show={parsedOptions.section?.show} title={parsedOptions.section?.title} forceHeadingInMarkup />
        <PropertyMapContent />
      </div>
    </WidgetProvider>
  )
}
